import type React from 'react';
import { AssetManifestClient, resolveEntryUrl } from '@goodtime/engine';
import { Icon } from '../../Icon';
import {
  BIOMES,
  isSlotNodeType,
  medalForStars,
  THEMED_SLOTS,
  type Biome,
  type SlotMedal,
  type WorldMapNode as MapNodeData,
  type WorldNodeType,
} from '@goodtime/shared';

/** Star colours per earned medal — filled star fill + its outline.
 *  Drives the mastery meter on slot tiles (M1, 2026-06-26). */
const MEDAL_STAR_COLORS: Record<
  Exclude<SlotMedal, 'none'>,
  { fill: string; stroke: string }
> = {
  bronze: { fill: '#e0934a', stroke: '#8a5320' },
  silver: { fill: '#e4e9f0', stroke: '#97a0ad' },
  gold: { fill: '#ffd24a', stroke: '#bd8c10' },
};

// Stage 3 install-excision (2026-06-06) — see MapNode.tsx for the
// same pattern + rationale. {@code boss} maps to {@code climb.png}
// here (producer audit Phase 3.4 — distinct icon from MapHud's
// multiplayer-crown boss) so the world map doesn't read as two
// multiplayer entries.
const SLOT_BADGE_PATH = 'map_node/slot.png';

function badgeUrl(type: string): string | undefined {
  const path = `map_node/${type}.png`;
  const manifest = AssetManifestClient.shared.getManifest();
  if (manifest && !manifest.entries[path]) return undefined;
  return resolveEntryUrl({ path }) ?? undefined;
}

/** Render-time resolver — see MapNode.tsx iconForType for the same
 *  rationale (manifest-client fallback prefix is wired post-mount,
 *  so module-level lookups return empty). {@code boss} maps to
 *  {@code climb.png} per the v2 design split. */
function iconForType(type: WorldNodeType | string): string {
  if (type === 'boss') return badgeUrl('climb') ?? '';
  if (type in THEMED_SLOTS) {
    // THEMED_SLOTS keys already end in '_reels' — see MapNode.tsx.
    return (
      badgeUrl(type) ??
      resolveEntryUrl({ path: SLOT_BADGE_PATH }) ??
      ''
    );
  }
  return badgeUrl(type) ?? '';
}

/** Per-biome ground-mound palette. The mound is the small platter the
 * coin face sits on — its colour should read as the local environment
 * (grass, sand, snow, dust, asphalt) rather than the kingdom's green
 * everywhere. Three radial-gradient stops + an inner-bottom shadow. */
type MoundPalette = {
  light: string;
  mid: string;
  dark: string;
  innerShadow: string;
};
const MOUND_PALETTE_BY_BIOME: Record<Biome['id'], MoundPalette> = {
  // grass
  kingdom: {
    light: '#A8DC60',
    mid: '#5FA02E',
    dark: '#3E7A3E',
    innerShadow: 'rgba(20,80,30,0.5)',
  },
  // sun-bleached beach sand
  cove: {
    light: '#F4D9A0',
    mid: '#D9A85F',
    dark: '#A47730',
    innerShadow: 'rgba(120,80,30,0.5)',
  },
  // packed snow
  frost: {
    light: '#F2F8FF',
    mid: '#C5D9F1',
    dark: '#7A98C2',
    innerShadow: 'rgba(60,90,140,0.45)',
  },
  // dusty desert ground
  frontier: {
    light: '#E5C58D',
    mid: '#B68B4A',
    dark: '#7A4F1F',
    innerShadow: 'rgba(80,50,15,0.5)',
  },
  // wet neon-lit asphalt
  neon: {
    light: '#5C5470',
    mid: '#353047',
    dark: '#1A1726',
    innerShadow: 'rgba(0,0,0,0.6)',
  },
  // golden desert sand
  egypt: {
    light: '#F6DDA0',
    mid: '#D9A94E',
    dark: '#A6742C',
    innerShadow: 'rgba(120,80,25,0.5)',
  },
  // pink candy frosting
  candy: {
    light: '#FFC9E6',
    mid: '#F58FC4',
    dark: '#C44D8E',
    innerShadow: 'rgba(150,40,100,0.5)',
  },
};
const FALLBACK_MOUND = MOUND_PALETTE_BY_BIOME.kingdom;

export interface MapNodePlatformProps {
  node: MapNodeData;
  /** Pixel left position inside the L1 layer. */
  leftPx: number;
  /** Pixel bottom position (distance from the bottom edge of L1). */
  bottomPx: number;
  isCurrent: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  /** Gem-economy lock state (T1, 2026-06-04). When {@code true} the
   *  tile renders the chained-padlock locked icon (same visual as
   *  {@link isLocked}) but stays tappable — onClick still fires so
   *  the host can route into {@code SlotUnlockSheet}. Used by the
   *  world map to mark slot tiles the player hasn't bought yet
   *  without making them click-dead. */
  isGemLocked?: boolean;
  /** M1 slot mastery — stars earned on this slot (0–3). Renders a small
   *  bronze/silver/gold star meter above the coin when ≥1. Ignored for
   *  non-slot nodes. Defaults to 0 (no meter). */
  stars?: number;
  onClick: (node: MapNodeData, event?: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * One node in the V2 map — coin-style level button on a small
 * biome-tinted mound. Reuses the existing {@code map_node/<type>.png}
 * commissioned coin face; the mound is CSS-only (radial gradient +
 * inner shadow), with the gradient stops sourced from
 * {@link MOUND_PALETTE_BY_BIOME} so the platter reads as the local
 * ground (grass / sand / snow / dust / asphalt).
 *
 * <p>Layout: bottomPx anchors the platform from the floor of the L1
 * strip, leftPx places it horizontally. The mound is rendered BEHIND
 * the coin so the coin sits on top.
 */
export function MapNodePlatform({
  node,
  leftPx,
  bottomPx,
  isCurrent,
  isCompleted,
  isLocked,
  isGemLocked = false,
  stars = 0,
  onClick,
}: MapNodePlatformProps) {
  const locked = isLocked || node.type === 'locked';
  // M1 slot mastery — show the star meter on playable slot tiles that
  // have earned at least one star. Non-slot nodes + locked tiles skip it.
  // {@code isSlotNodeType} covers the generic free 'slot' AND every themed
  // variant (THEMED_SLOTS alone would miss the free first slot per biome).
  const isSlotNode = isSlotNodeType(node.type);
  const showStars = isSlotNode && !locked && stars > 0;
  const medal = medalForStars(stars);
  const starColor =
    medal === 'none' ? null : MEDAL_STAR_COLORS[medal];
  // Gem-locked: the SLOT tile shows the chained-padlock icon (same as
  // a fully-locked tile) but onClick still fires — the host routes
  // into SlotUnlockSheet. Distinguishes the gem-economy "tap to buy"
  // state from the "click-dead, coming soon" state of node.type='locked'.
  const showLockedIcon = locked || isGemLocked;
  const isBoss = node.type === 'boss';
  const size = isBoss ? 140 : 110;
  const iconSrc = showLockedIcon ? iconForType('locked') : iconForType(node.type);
  const biomeId = BIOMES[node.biome]?.id;
  const mound = (biomeId && MOUND_PALETTE_BY_BIOME[biomeId]) || FALLBACK_MOUND;

  return (
    <button
      type="button"
      onClick={(event) => !locked && onClick(node, event)}
      style={{
        position: 'absolute',
        left: leftPx,
        bottom: bottomPx,
        transform: 'translate(-50%, 0)',
        width: size,
        height: size + 40,
        border: 'none',
        background: 'transparent',
        cursor: locked ? 'default' : 'pointer',
        padding: 0,
        zIndex: isCurrent ? 30 : 20,
      }}
      aria-label={node.name}
    >
      {/* drop shadow under the mound */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: size * 1.4,
          height: 18,
          borderRadius: '50%',
          background:
            'radial-gradient(ellipse, rgba(0,0,0,0.45), rgba(0,0,0,0.12) 55%, transparent 78%)',
          filter: 'blur(2.5px)',
        }}
      />
      {/* biome-tinted ground mound — chunky 3D rounded platter */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 4,
          transform: 'translateX(-50%)',
          width: size * 1.25,
          height: 30,
          borderRadius: '50%',
          background:
            `radial-gradient(ellipse at 50% 25%, ${mound.light}, ${mound.mid} 60%, ${mound.dark} 100%)`,
          boxShadow:
            `inset 0 -4px 5px ${mound.innerShadow}, inset 0 3px 3px rgba(255,255,255,0.35)`,
        }}
      />
      {/* current-node glow ring — paints behind the coin; box-shadow
          comes from the gtPulse keyframes injected by WorldMapV2.
          gtFloat is layered on so the ring bobs in sync with the coin
          (which also runs gtFloat) — they touch different properties
          (transform vs box-shadow) so the two animations compose.
          The div is sized smaller than the coin so the box-shadow's
          bright spread-ring sits INSIDE the coin's silhouette (hidden
          behind it) and only the soft outer blur falloff shows — that
          eliminates the visible gap between coin and halo. */}
      {isCurrent && !locked && (() => {
        // Glow ring sized 0.78× the coin so the box-shadow's bright
        // inner spread (the {@code 0 0 14px 2px} pass in gtPulse)
        // tucks INSIDE the coin's silhouette. Only the soft outer
        // blur falloff (the {@code 0 0 28-56px} pass) is visible past
        // the coin's edge — which reads as a continuous halo with no
        // gap between coin and glow. Producer audit (Phase 2.2) flagged
        // the prior 1:1 sizing as creating a visible band between the
        // gold halo and the coin's golden edge.
        const glowSize = Math.round(size * 0.78);
        // Center the glow on the coin: coin is anchored at bottom: 18
        // and is `size` tall; the glow is `glowSize` tall. Offset the
        // glow up by half the size difference so their centers align.
        const glowBottom = 18 + Math.round((size - glowSize) / 2);
        return (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              bottom: glowBottom,
              transform: 'translateX(-50%)',
              width: glowSize,
              height: glowSize,
              borderRadius: '50%',
              pointerEvents: 'none',
              animation:
                'gtFloat 2s ease-in-out infinite, gtPulse 1.2s ease-in-out infinite',
            }}
          />
        );
      })()}
      {/* coin */}
      <img
        src={iconSrc}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 18,
          transform: 'translateX(-50%)',
          width: size,
          height: size,
          filter: locked ? 'none' : 'drop-shadow(0 5px 8px rgba(0,0,0,0.35))',
          animation: isCurrent ? 'gtFloat 2s ease-in-out infinite' : 'none',
        }}
      />
      {/* completed-node check stamp — gold disc at the south-east rim
          of the coin. Suppressed on the current node so the pulse ring
          owns the visual focus. */}
      {isCompleted && !locked && !isCurrent && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            right: 0,
            bottom: 14,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 35% 30%, #ffe98a, #d8a228 70%, #8a5d10)',
            border: '2px solid #fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 16,
            fontWeight: 900,
            lineHeight: 1,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Icon name="check" size={16} alt="" />
        </div>
      )}
      {/* M1 slot mastery — 3-star meter, sitting just above the coin like
          a crown. Filled stars take the medal colour (bronze→silver→gold);
          unearned stars stay a faint outline so the goal reads at a glance. */}
      {showStars && starColor && (
        <div
          aria-label={`${stars} of 3 stars`}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 18 + size - 10,
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            padding: '1px 5px',
            borderRadius: 9,
            background: 'rgba(0,0,0,0.38)',
            pointerEvents: 'none',
            zIndex: 2,
            lineHeight: 1,
          }}
        >
          {[0, 1, 2].map((i) => {
            const filled = i < stars;
            return (
              <span
                key={i}
                style={{
                  fontSize: 13,
                  color: filled ? starColor.fill : 'rgba(255,255,255,0.30)',
                  textShadow: filled
                    ? `0 1px 0 ${starColor.stroke}, 0 0 3px rgba(0,0,0,0.5)`
                    : 'none',
                }}
              >
                ★
              </span>
            );
          })}
        </div>
      )}
      {/* label */}
      <div
        style={{
          position: 'absolute',
          bottom: -22,
          left: '50%',
          transform: 'translateX(-50%)',
          whiteSpace: 'nowrap',
          fontSize: 14,
          fontWeight: 800,
          color: '#fff',
          textShadow: '0 2px 0 rgba(0,0,0,0.75), 0 0 8px rgba(0,0,0,0.7)',
          letterSpacing: 0.3,
          fontFamily: '"Fredoka", system-ui',
          pointerEvents: 'none',
        }}
      >
        {node.name}
      </div>
    </button>
  );
}
